import { PlusOutlined } from '@ant-design/icons'

import { createBannerApi } from '@eastcoal/api/content/bannerOperation/createBanner'
import { queryBannerApi } from '@eastcoal/api/content/bannerOperation/queryBanner'
import { updateBannerApi } from '@eastcoal/api/content/bannerOperation/updateBanner'

import { PageContainer } from '@eastcoal/common/components/Layout/PageContainer'
import { ObjectAcl } from '@eastcoal/common/enums/ObjectAcl'
import { useFeedback } from '@eastcoal/common/hooks/useFeedback'
import { useUrlParams } from '@eastcoal/common/hooks/useUrlParams'
import { mapNumberEnumOptions } from '@eastcoal/common/utils/enums'

import { CrudForm } from '@eastcoal/formily/components/CrudForm'
import { BackButton } from '@eastcoal/formily/components/CrudForm/shared/BackButton'
import { SaveButton } from '@eastcoal/formily/components/CrudForm/shared/SaveButton'
import { SubmitButton } from '@eastcoal/formily/components/CrudForm/shared/SubmitButton'
import { DateRangePicker } from '@eastcoal/formily/components/DatePicker'
import { FormItem } from '@eastcoal/formily/components/FormItem'
import { Input } from '@eastcoal/formily/components/Input'
import { InputNumber } from '@eastcoal/formily/components/InputNumber'
import { Select } from '@eastcoal/formily/components/Select'
import { UploadFile } from '@eastcoal/formily/components/UploadFile'

import { useNavigate } from '@eastcoal/router/exports/react-router'
import { DeviceType } from '@eastcoal/shared/enums/DeviceType'
import { PageName, PageNameTextMap } from '@eastcoal/shared/enums/PageName'
import {
  PagePosition,
  PagePositionTextMap
} from '@eastcoal/shared/enums/PagePosition'

import { BannerFormExposedProps, BannerFormProps } from './interface'

const schema: BannerFormProps['schema'] = [
  {
    name: 'ossIds',
    title: '图片',
    type: 'array',
    required: true,
    'x-component': UploadFile,
    'x-component-props': {
      multiple: false,
      maxCount: 1,
      objectAcl: ObjectAcl.PUBLIC_READ,
      listType: 'picture-card',
      accept: '.png, .jpg, .jpeg',
      children: <PlusOutlined />,
      maxFileSize: 20 * 1024 * 1024
    },
    'x-decorator': FormItem
  },
  {
    name: 'sort',
    title: '展示顺序',
    type: 'number',
    required: true,
    'x-component': InputNumber,
    'x-decorator': FormItem,
    'x-decorator-props': {
      tooltip:
        '序号仅限输入整数，数值越小越优先展示；序号相同时，优先展示最新编辑的'
    }
  },
  {
    name: '[displayStartTime,displayEndTime]',
    title: '展示时间',
    type: 'array',
    'x-component': DateRangePicker,
    'x-component-props': {
      showTime: true
    },
    'x-decorator': FormItem
  },
  {
    name: 'navigateUrl',
    title: '跳转链接',
    type: 'string',
    'x-component': Input,
    'x-validator': [
      {
        format: 'url'
      }
    ],
    'x-decorator': FormItem
  },
  {
    name: 'device',
    title: '终端',
    type: 'string',
    default: DeviceType.PC,
    'x-display': 'hidden'
  },
  {
    name: 'pageName',
    title: '所属页面',
    type: 'string',
    'x-component': Select,
    'x-decorator': FormItem,
    enum: mapNumberEnumOptions(PageNameTextMap)
  },
  {
    name: 'pagePosition',
    title: '所在位置',
    type: 'number',
    'x-component': Select,
    'x-decorator': FormItem,
    enum: mapNumberEnumOptions(PagePositionTextMap)
  }
]

const readApi: BannerFormProps['readApi'] = ({ id }) => {
  return {
    api: async () => {
      const data = await queryBannerApi({ id })
      return {
        ...data,
        ossIds: [data.ossId]
      }
    },
    ready: id
  }
}

const updateApi: BannerFormProps['updateApi'] = (values, { submitType }) =>
  updateBannerApi({
    ...values,
    ossId: values.ossIds[0],
    status: submitType
  })

const createApi: BannerFormProps['updateApi'] = (values, { submitType }) =>
  createBannerApi({
    ...values,
    ossId: values.ossIds[0],
    status: submitType
  })

export const BannerForm = ({ mode }: BannerFormExposedProps) => {
  const { pageName = PageName.HOME } = useUrlParams()

  const navigate = useNavigate()
  const { message } = useFeedback()

  const handleSubmitSuccess = () => {
    message.success('操作成功')
    navigate(-1)
  }

  return (
    <PageContainer>
      <CrudForm
        createApi={createApi}
        mode={mode}
        name='bannerForm'
        readApi={readApi}
        schema={schema}
        updateApi={updateApi}
        actions={[
          <BackButton key='backButton' />,
          <SaveButton key='saveButton' />,
          <SubmitButton key='sumbitButton'>上架</SubmitButton>
        ]}
        grid={{
          columns: 1
        }}
        initialValues={{
          pageName: Number.parseInt(pageName) as PageName,
          pagePosition: PagePosition.TOP
        }}
        layout={{
          wrapperWidth: 400
        }}
        onSuccess={handleSubmitSuccess}
      />
    </PageContainer>
  )
}
